import { Injectable, OnDestroy, Inject, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface KeyboardShortcut {
  /** The key to match (e.g. 'n', 'Escape', 'ArrowRight', '?', '1') */
  key: string;
  alt?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  /** Return false to let lower-priority handlers try. Void/true consumes the event. */
  handler: () => boolean | void;
  /** If true, fires even when an input/textarea/select is focused */
  allowInInput?: boolean;
  /** Translation key for the cheatsheet (e.g. 'new_order') */
  description?: string;
  /** Category key for cheatsheet grouping (e.g. 'navigation', 'form', 'global') */
  category?: string;
  /** Higher = checked first. Default 0 */
  priority?: number;
}

export interface ShortcutDescription {
  combo: string;
  description: string;
  category: string;
}

@Injectable({ providedIn: 'root' })
export class KeyboardShortcutService implements OnDestroy {
  private shortcuts: KeyboardShortcut[] = [];
  private removeListener: (() => void) | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone,
  ) {
    // Attach outside zone (avoid triggering CD on every keypress),
    // but run matched handlers inside zone so Angular detects state changes.
    if (typeof window !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        const handler = (e: KeyboardEvent) => this.handleKeydown(e);
        this.document.addEventListener('keydown', handler);
        this.removeListener = () => this.document.removeEventListener('keydown', handler);
      });
    }
  }

  ngOnDestroy(): void {
    this.removeListener?.();
  }

  register(shortcut: KeyboardShortcut): () => void {
    this.shortcuts.push(shortcut);
    return () => {
      const idx = this.shortcuts.indexOf(shortcut);
      if (idx >= 0) this.shortcuts.splice(idx, 1);
    };
  }

  registerMany(shortcuts: KeyboardShortcut[]): () => void {
    const fns = shortcuts.map(s => this.register(s));
    return () => fns.forEach(fn => fn());
  }

  getDescriptions(): ShortcutDescription[] {
    return this.shortcuts
      .filter(s => s.description)
      .map(s => ({
        combo: this.formatCombo(s),
        description: s.description!,
        category: s.category || 'global',
      }));
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Skip if a SweetAlert dialog is open
    if (this.document.querySelector('.swal2-container')) return;

    const target = event.target as HTMLElement;
    const inInput = this.isInput(target);

    const sorted = [...this.shortcuts].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0)
    );

    for (const s of sorted) {
      if (!this.matches(event, s)) continue;
      if (inInput && !s.allowInInput) continue;

      // Run handler inside Angular zone so change detection picks up state changes
      let result: boolean | void;
      this.zone.run(() => { result = s.handler(); });
      if (result! !== false) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  }

  private matches(event: KeyboardEvent, s: KeyboardShortcut): boolean {
    if (event.key !== s.key && event.key.toLowerCase() !== s.key.toLowerCase()) return false;
    if (!!s.alt !== event.altKey) return false;
    if (!!s.ctrl !== (event.ctrlKey || event.metaKey)) return false;
    // Shift: only enforce if explicitly specified (allows '?' without specifying shift)
    if (s.shift !== undefined && s.shift !== event.shiftKey) return false;
    return true;
  }

  private isInput(el: HTMLElement): boolean {
    const tag = el.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
  }

  private formatCombo(s: KeyboardShortcut): string {
    const parts: string[] = [];
    if (s.ctrl) parts.push('Ctrl');
    if (s.alt) parts.push('Alt');
    if (s.shift) parts.push('Shift');
    const names: Record<string, string> = {
      ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓',
      Escape: 'Esc', ' ': 'Space',
    };
    parts.push(names[s.key] || s.key.toUpperCase());
    return parts.join(' + ');
  }
}
